// Encrypted storage of the recovery phrase (and contacts) in the browser.
//
// A random 256-bit vault key K encrypts the data with AES-GCM. K itself is
// stored only wrapped (encrypted):
//   - with a key derived from the user's passcode (PBKDF2-SHA256, 600 000
//     iterations, random salt): always present;
//   - optionally with a key derived from a passkey's PRF output (Face ID /
//     Touch ID): lets the platform authenticator unlock without the passcode.
// Nothing sensitive is ever stored in clear. Everything uses WebCrypto.

const VAULT_KEY = 'coinwow.vault.v1';

export interface PendingFaceID {
  credentialId: Uint8Array<ArrayBuffer>;
  prfSalt: Uint8Array<ArrayBuffer>;
  output?: ArrayBuffer;
}
const ATTEMPTS_KEY = 'coinwow.pin.attempts';
const PBKDF2_ITERATIONS = 600_000;
const encoder = new TextEncoder();
const enc = { encode: (s: string): Uint8Array<ArrayBuffer> => new Uint8Array(encoder.encode(s)) };
const dec = new TextDecoder();

interface Box {
  iv: string;
  ct: string;
}

interface VaultFile {
  v: 1;
  salt: string;
  iterations: number;
  pinWrap: Box;
  phrase: Box;
  contacts?: Box;
  prf?: { credentialId: string; salt: string; wrap: Box };
}

const b64 = {
  enc(b: Uint8Array): string {
    let s = '';
    for (const x of b) s += String.fromCharCode(x);
    return btoa(s);
  },
  dec(s: string): Uint8Array<ArrayBuffer> {
    const bin = atob(s);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  },
};

const rand = (n: number): Uint8Array<ArrayBuffer> => crypto.getRandomValues(new Uint8Array(n));

async function aesKey(raw: Uint8Array<ArrayBuffer>, usages: KeyUsage[] = ['encrypt', 'decrypt']): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw, 'AES-GCM', false, usages);
}

async function seal(key: CryptoKey, data: Uint8Array<ArrayBuffer>): Promise<Box> {
  const iv = rand(12);
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data));
  return { iv: b64.enc(iv), ct: b64.enc(ct) };
}

async function open(key: CryptoKey, box: Box): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: b64.dec(box.iv) }, key, b64.dec(box.ct)));
}

async function pinKey(pin: string, salt: Uint8Array<ArrayBuffer>, iterations: number): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', enc.encode(pin), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

async function prfKey(prfOutput: ArrayBuffer): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', prfOutput, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: enc.encode('coinwow-vault'), info: enc.encode('prf-wrap-v1') },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

function read(): VaultFile | null {
  try {
    const s = localStorage.getItem(VAULT_KEY);
    return s ? (JSON.parse(s) as VaultFile) : null;
  } catch {
    return null;
  }
}

function write(v: VaultFile) {
  localStorage.setItem(VAULT_KEY, JSON.stringify(v));
}

/** An unlocked vault: holds the AES key in memory (non-extractable). */
export class OpenVault {
  constructor(private readonly key: CryptoKey) {}

  /** Keeps the (non-extractable) key for a short session so a reload doesn't ask again. */
  remember() {
    void Session.save(this.key);
  }

  async phrase(): Promise<string> {
    const v = read();
    if (!v) throw new Error('no wallet');
    return dec.decode(await open(this.key, v.phrase));
  }

  async contacts<T>(): Promise<T | null> {
    const v = read();
    if (!v?.contacts) return null;
    return JSON.parse(dec.decode(await open(this.key, v.contacts))) as T;
  }

  async saveContacts(value: unknown) {
    const v = read();
    if (!v) return;
    v.contacts = await seal(this.key, enc.encode(JSON.stringify(value)));
    write(v);
  }
}

export const Vault = {
  exists(): boolean {
    return read() !== null;
  },

  hasFaceID(): boolean {
    return !!read()?.prf;
  },

  /** Creates the vault for a new or restored wallet. */
  async create(phrase: string, pin: string): Promise<OpenVault> {
    const raw = rand(32);
    const key = await aesKey(raw);
    const salt = rand(16);
    const wrapper = await pinKey(pin, salt, PBKDF2_ITERATIONS);
    const file: VaultFile = {
      v: 1,
      salt: b64.enc(salt),
      iterations: PBKDF2_ITERATIONS,
      pinWrap: await seal(wrapper, raw),
      phrase: await seal(key, enc.encode(phrase)),
    };
    raw.fill(0);
    write(file);
    localStorage.removeItem(ATTEMPTS_KEY);
    return new OpenVault(key);
  },

  /** Seconds to wait before the next passcode attempt (0 = allowed now). */
  lockoutRemaining(): number {
    const a = attempts();
    return Math.max(0, Math.ceil((a.until - Date.now()) / 1000));
  },

  /** Attempts left before the next pause. */
  attemptsLeft(): number {
    return Math.max(0, 5 - (attempts().count % 5));
  },

  /** Unlocks with the passcode, or returns null (wrong passcode). */
  async unlockWithPin(pin: string): Promise<OpenVault | null> {
    const v = read();
    if (!v) return null;
    if (Vault.lockoutRemaining() > 0) return null;
    try {
      const wrapper = await pinKey(pin, b64.dec(v.salt), v.iterations);
      const raw = await open(wrapper, v.pinWrap);
      const key = await aesKey(raw);
      raw.fill(0);
      localStorage.removeItem(ATTEMPTS_KEY);
      return new OpenVault(key);
    } catch {
      const a = attempts();
      a.count += 1;
      // A pause after every 5 wrong passcodes: 30 s, 1 min, 5 min, 15 min, then 1 h.
      if (a.count % 5 === 0) {
        const steps = [30, 60, 300, 900, 3600];
        a.until = Date.now() + steps[Math.min(a.count / 5 - 1, steps.length - 1)] * 1000;
      }
      localStorage.setItem(ATTEMPTS_KEY, JSON.stringify(a));
      return null;
    }
  },

  /** Checks a passcode without changing anything (for confirmations). */
  async checkPin(pin: string): Promise<boolean> {
    return (await Vault.unlockWithPin(pin)) !== null;
  },

  // ---- Face ID / Touch ID (WebAuthn passkey with the PRF extension) ----

  async faceIDSupported(): Promise<boolean> {
    try {
      if (!window.PublicKeyCredential || !window.isSecureContext) return false;
      const pk = PublicKeyCredential as any;
      if (!(await pk.isUserVerifyingPlatformAuthenticatorAvailable?.())) return false;
      if (pk.getClientCapabilities) {
        const caps = await pk.getClientCapabilities();
        if (caps && caps['extension:prf'] === false) return false;
      }
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Step 1 of Face ID: creates the passkey. Must be called directly from a tap
   * (browsers only show the Face ID prompt for a user action).
   */
  async startFaceID(rpName = 'COINWOW'): Promise<PendingFaceID | null> {
    const prfSalt = rand(32);
    const cred = (await navigator.credentials.create({
      publicKey: {
        rp: { name: rpName, id: location.hostname },
        user: { id: rand(16), name: 'COINWOW', displayName: 'COINWOW' },
        challenge: rand(32),
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 },
        ],
        authenticatorSelection: { authenticatorAttachment: 'platform', residentKey: 'preferred', userVerification: 'required' },
        timeout: 60_000,
        extensions: { prf: { eval: { first: prfSalt } } } as any,
      },
    })) as PublicKeyCredential | null;
    if (!cred) return null;
    const ext = (cred.getClientExtensionResults() as any).prf;
    if (ext && ext.enabled === false) return null;
    return { credentialId: new Uint8Array(cred.rawId), prfSalt, output: ext?.results?.first };
  },

  /**
   * Step 2: wraps the vault key with the passkey's secret. If the browser did
   * not return it at creation, Face ID is asked once more: call from a tap.
   */
  async finishFaceID(pin: string, p: PendingFaceID): Promise<boolean> {
    const output = p.output ?? (await prfEvaluate(p.credentialId, p.prfSalt));
    if (!output) return false;
    const v = read();
    if (!v) return false;
    let raw: Uint8Array<ArrayBuffer>;
    try {
      raw = await open(await pinKey(pin, b64.dec(v.salt), v.iterations), v.pinWrap);
    } catch {
      return false;
    }
    const box = await seal(await prfKey(output), raw);
    raw.fill(0);
    v.prf = { credentialId: b64.enc(p.credentialId), salt: b64.enc(p.prfSalt), wrap: box };
    write(v);
    return true;
  },

  async unlockWithFaceID(): Promise<OpenVault | null> {
    const v = read();
    if (!v?.prf) return null;
    try {
      const output = await prfEvaluate(b64.dec(v.prf.credentialId), b64.dec(v.prf.salt));
      if (!output) return null;
      const raw = await open(await prfKey(output), v.prf.wrap);
      const key = await aesKey(raw);
      raw.fill(0);
      return new OpenVault(key);
    } catch {
      return null;
    }
  },

  disableFaceID() {
    const v = read();
    if (!v) return;
    delete v.prf;
    write(v);
  },

  /** Reopens a recent session (reload within `maxAgeMs` of the last activity), or null. */
  async resume(maxAgeMs: number): Promise<OpenVault | null> {
    if (!read()) { await Session.clear(); return null; }
    const sess = await Session.load();
    if (!sess) return null;
    if (Date.now() - sess.lastActive > maxAgeMs || sess.lastActive > Date.now() + 60_000) {
      await Session.clear();
      return null;
    }
    const ov = new OpenVault(sess.key);
    try {
      await ov.phrase(); // the key must still match this vault
    } catch {
      await Session.clear();
      return null;
    }
    return ov;
  },

  /** Records activity so the session stays valid. */
  touchSession() {
    Session.touch();
  },

  endSession() {
    void Session.clear();
  },

  /** Erases the wallet from this browser. */
  destroy() {
    void Session.clear();
    localStorage.removeItem(VAULT_KEY);
    localStorage.removeItem(ATTEMPTS_KEY);
  },
};

function attempts(): { count: number; until: number } {
  try {
    const a = JSON.parse(localStorage.getItem(ATTEMPTS_KEY) || 'null');
    if (a && typeof a.count === 'number' && typeof a.until === 'number') return a;
  } catch {}
  return { count: 0, until: 0 };
}

async function prfEvaluate(credentialId: Uint8Array<ArrayBuffer>, salt: Uint8Array<ArrayBuffer>): Promise<ArrayBuffer | undefined> {
  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: rand(32),
      rpId: location.hostname,
      allowCredentials: [{ type: 'public-key', id: credentialId }],
      userVerification: 'required',
      timeout: 60_000,
      extensions: { prf: { eval: { first: salt } } } as any,
    },
  })) as PublicKeyCredential | null;
  return (assertion?.getClientExtensionResults() as any)?.prf?.results?.first;
}

// ---- Short session (IndexedDB) ----
// The AES key is stored as a non-extractable CryptoKey: the page can use it but
// never read its bytes. It is deleted on lock, on expiry and when the wallet is erased.

interface SessionRecord { key: CryptoKey; lastActive: number }

const Session = {
  db: null as Promise<IDBDatabase> | null,
  lastTouch: 0,

  open(): Promise<IDBDatabase> {
    if (!this.db) {
      this.db = new Promise((resolve, reject) => {
        const r = indexedDB.open('coinwow-session', 1);
        r.onupgradeneeded = () => r.result.createObjectStore('s');
        r.onsuccess = () => resolve(r.result);
        r.onerror = () => reject(r.error);
      });
      this.db.catch(() => { this.db = null; });
    }
    return this.db;
  },

  async tx<T>(mode: IDBTransactionMode, fn: (st: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
    try {
      const db = await this.open();
      return await new Promise<T | undefined>((resolve, reject) => {
        const t = db.transaction('s', mode);
        const req = fn(t.objectStore('s'));
        t.oncomplete = () => resolve(req ? req.result : undefined);
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error);
      });
    } catch {
      return undefined;
    }
  },

  async save(key: CryptoKey) {
    if (typeof indexedDB === 'undefined') return;
    this.lastTouch = Date.now();
    await this.tx('readwrite', (st) => { st.put({ key, lastActive: this.lastTouch } satisfies SessionRecord, 'current'); });
  },

  touch() {
    if (typeof indexedDB === 'undefined' || Date.now() - this.lastTouch < 10_000) return;
    this.lastTouch = Date.now();
    const now = this.lastTouch;
    void this.tx('readwrite', (st) => {
      const g = st.get('current');
      g.onsuccess = () => { if (g.result) st.put({ ...g.result, lastActive: now }, 'current'); };
    });
  },

  async load(): Promise<SessionRecord | null> {
    if (typeof indexedDB === 'undefined') return null;
    const r = await this.tx<SessionRecord>('readonly', (st) => st.get('current'));
    return r && r.key instanceof CryptoKey && typeof r.lastActive === 'number' ? r : null;
  },

  async clear() {
    this.lastTouch = 0;
    if (typeof indexedDB === 'undefined') return;
    await this.tx('readwrite', (st) => { st.delete('current'); });
  },
};
